// File: src/components/admin/scheduler/ScheduleBuilder.js
// Copyright (c) 2025 DefoAI UG (haftungsbeschraenkt)
// All rights reserved.
//
// Visual schedule & trigger configuration builder for Analysis Tasks (Issue #68)
// Replaces the inline schedule tab with a form-based UI for configuring
// trigger types, intervals, event triggers, condition rules, and delay.

import React, { useState, useMemo, useCallback } from 'react';
import {
    Box, Paper, Typography, Button, IconButton, Chip, Alert, Collapse,
    TextField, MenuItem, FormControlLabel, Checkbox, ToggleButton, ToggleButtonGroup,
    Grid, Divider, ListSubheader, Select, FormControl, InputLabel, Dialog,
    DialogTitle, DialogContent, DialogContentText, DialogActions, FormGroup,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Schedule as ScheduleIcon,
    Bolt as BoltIcon,
    FilterAlt as FilterAltIcon,
    Merge as MergeIcon,
} from '@mui/icons-material';
import JsonViewTab from '../common/JsonViewTab';
import { FIELD_METADATA, CATEGORY_LABELS, CATEGORY_ORDER } from '../shared/AudienceRuleBuilder';

// ─── Constants ──────────────────────────────────────────────────────────────

const TRIGGER_TYPE_OPTIONS = [
    { value: 'schedule',    label: 'Scheduled',    icon: <ScheduleIcon />,   description: 'Runs on a recurring schedule' },
    { value: 'event',       label: 'Event',        icon: <BoltIcon />,       description: 'Triggered by user/system events' },
    { value: 'condition',   label: 'Condition',    icon: <FilterAltIcon />,  description: 'Runs when conditions are met' },
    { value: 'combination', label: 'Combination',  icon: <MergeIcon />,      description: 'Scheduled + event triggers' },
];

const AVAILABLE_EVENT_TRIGGERS = [
    { id: 'user_signup',              label: 'User Signup',              description: 'When a new user creates an account' },
    { id: 'first_campaign_created',   label: 'First Campaign Created',  description: 'When a user creates their first campaign' },
    { id: 'campaign_created',         label: 'Campaign Created',        description: 'When any campaign is created' },
    { id: 'subscription_created',     label: 'Subscription Created',    description: 'When a user subscribes to a plan' },
    { id: 'subscription_cancelled',   label: 'Subscription Cancelled',  description: 'When a user cancels their subscription' },
    { id: 'limit_reached',            label: 'Limit Reached',           description: 'When a user hits a plan limit' },
    { id: 'platform_connected',       label: 'Platform Connected',      description: 'When a user connects a platform (e.g. Google Ads)' },
    { id: 'sync_completed',           label: 'Sync Completed',          description: 'When a platform sync finishes successfully' },
    { id: 'user_inactive',            label: 'User Inactive',           description: 'When a user has been inactive for a configured period' },
];

const TIME_UNITS = [
    { value: 'm', label: 'Minutes' },
    { value: 'h', label: 'Hours' },
    { value: 'd', label: 'Days' },
];

const INTERVAL_PRESETS = [
    { label: '1h',  value: 1,  unit: 'h' },
    { label: '6h',  value: 6,  unit: 'h' },
    { label: '12h', value: 12, unit: 'h' },
    { label: '24h', value: 24, unit: 'h' },
    { label: '7d',  value: 7,  unit: 'd' },
];

// Valid intervals accepted by the backend (mirrors ScheduleDefinition.VALID_INTERVALS)
const VALID_INTERVALS = new Set([
    '1m', '5m', '15m', '30m',
    '1h', '2h', '6h', '12h', '24h',
    '7d',
    // Short intervals for testing
    '5s', '10s', '30s',
]);

const DELAY_PRESETS = [
    { label: 'None', value: 0, unit: 'h' },
    { label: '5m',   value: 5,  unit: 'm' },
    { label: '15m',  value: 15, unit: 'm' },
    { label: '30m',  value: 30, unit: 'm' },
    { label: '1h',   value: 1,  unit: 'h' },
    { label: '2h',   value: 2,  unit: 'h' },
    { label: '4h',   value: 4,  unit: 'h' },
];

// Condition operators differ from audience builder (is_true/is_false vs true/false)
const CONDITION_OPERATOR_LABELS = {
    '>':        'Greater than',
    '<':        'Less than',
    '=':        'Equals',
    '>=':       'At least',
    '<=':       'At most',
    'between':  'Between',
    'is_true':  'Is true',
    'is_false': 'Is false',
};

const NUMBER_OPERATORS = ['>', '<', '=', '>=', '<=', 'between'];
const BOOLEAN_OPERATORS = ['is_true', 'is_false'];

// Which sections are visible per trigger type
const FIELD_VISIBILITY = {
    schedule:    { interval: true,  events: false, conditions: false },
    event:       { interval: false, events: true,  conditions: false },
    condition:   { interval: false, events: false, conditions: true },
    combination: { interval: true,  events: true,  conditions: true },
};

// ─── Utility Functions ──────────────────────────────────────────────────────

function parseIntervalString(str) {
    if (!str) return { value: '', unit: 'h' };
    const match = String(str).match(/^(\d+)\s*(m|h|d)$/i);
    if (match) return { value: parseInt(match[1], 10), unit: match[2].toLowerCase() };
    // Try pure number (assume hours)
    const num = parseInt(str, 10);
    if (!isNaN(num)) return { value: num, unit: 'h' };
    return { value: '', unit: 'h' };
}

function buildIntervalString(value, unit) {
    if (value === '' || value === undefined || value === null || value === 0) return '';
    return `${value}${unit}`;
}

function getConditionOperatorsForField(fieldName) {
    const meta = FIELD_METADATA[fieldName];
    if (!meta) return [...NUMBER_OPERATORS, ...BOOLEAN_OPERATORS];
    if (meta.type === 'boolean') return BOOLEAN_OPERATORS;
    if (meta.type === 'number') return NUMBER_OPERATORS;
    return [...NUMBER_OPERATORS, ...BOOLEAN_OPERATORS];
}

function isValuelessOperator(op) {
    return op === 'is_true' || op === 'is_false';
}

// ─── Grouped field options for the Select ───────────────────────────────────

function getGroupedFieldOptions() {
    const groups = [];
    for (const cat of CATEGORY_ORDER) {
        const fields = Object.entries(FIELD_METADATA).filter(([, m]) => m.category === cat);
        if (fields.length > 0) {
            groups.push({ category: cat, fields });
        }
    }
    return groups;
}

// ─── Validation ─────────────────────────────────────────────────────────────

function validateConditionRule(rule, index) {
    const errors = [];
    if (!rule.field) {
        errors.push({ key: `cond-${index}-field`, message: 'Please select a field' });
    }
    if (!rule.operator) {
        errors.push({ key: `cond-${index}-operator`, message: 'Please select an operator' });
    }
    if (rule.operator && !isValuelessOperator(rule.operator)) {
        if (rule.operator === 'between') {
            const val = rule.value;
            if (!Array.isArray(val) || val.length !== 2) {
                errors.push({ key: `cond-${index}-value`, message: 'Between requires min and max values' });
            } else if (val[0] !== '' && val[1] !== '' && Number(val[0]) >= Number(val[1])) {
                errors.push({ key: `cond-${index}-value`, message: 'Min must be less than Max' });
            } else if (val[0] === '' || val[0] === undefined || val[1] === '' || val[1] === undefined) {
                errors.push({ key: `cond-${index}-value`, message: 'Please enter both min and max values' });
            }
        } else if (rule.value === '' || rule.value === undefined || rule.value === null) {
            errors.push({ key: `cond-${index}-value`, message: 'Please enter a value' });
        }
    }
    return errors;
}

function validateScheduleConfig(formData) {
    const errors = [];
    const visibility = FIELD_VISIBILITY[formData.triggerType] || {};

    if (visibility.interval) {
        const parsed = parseIntervalString(formData.interval);
        if (!parsed.value || parsed.value <= 0) {
            errors.push({ key: 'interval', message: 'Schedule interval is required' });
        } else if (formData.interval && !VALID_INTERVALS.has(formData.interval)) {
            errors.push({ key: 'interval', message: `Invalid interval "${formData.interval}". Use a preset or one of: ${[...VALID_INTERVALS].join(', ')}` });
        }
    }
    if (visibility.events) {
        if (!formData.eventTriggers || formData.eventTriggers.length === 0) {
            errors.push({ key: 'events', message: 'Select at least one event trigger', severity: 'warning' });
        }
    }
    if (visibility.conditions) {
        const rules = formData.conditionRules || [];
        if (rules.length === 0) {
            errors.push({ key: 'conditions-empty', message: 'Add at least one condition rule', severity: 'warning' });
        }
        rules.forEach((rule, i) => {
            errors.push(...validateConditionRule(rule, i));
        });
    }

    // Check for duplicate condition rules
    const rules = formData.conditionRules || [];
    rules.forEach((rule, i) => {
        if (rule.field && rule.operator) {
            const isDup = rules.some((other, j) =>
                j !== i && other.field === rule.field && other.operator === rule.operator
            );
            if (isDup) {
                errors.push({ key: `cond-${i}-dup`, message: 'Duplicate condition rule', severity: 'warning' });
            }
        }
    });

    return errors;
}

// ─── Summary Generator ──────────────────────────────────────────────────────

function generateTriggerSummary(formData) {
    const parts = [];
    const triggerOpt = TRIGGER_TYPE_OPTIONS.find(t => t.value === formData.triggerType);
    parts.push(`Trigger: ${triggerOpt?.label || formData.triggerType}`);

    const visibility = FIELD_VISIBILITY[formData.triggerType] || {};

    if (visibility.interval && formData.interval) {
        parts.push(`Interval: every ${formData.interval}`);
    }

    if (visibility.events && formData.eventTriggers?.length > 0) {
        const eventLabels = formData.eventTriggers.map(id => {
            const evt = AVAILABLE_EVENT_TRIGGERS.find(e => e.id === id);
            return evt?.label || id;
        });
        parts.push(`Events: ${eventLabels.join(', ')}`);
    }

    if (visibility.conditions && formData.conditionRules?.length > 0) {
        const condTexts = formData.conditionRules.map(rule => {
            const meta = FIELD_METADATA[rule.field];
            const fieldLabel = meta?.label || rule.field || '?';
            const opLabel = CONDITION_OPERATOR_LABELS[rule.operator] || rule.operator || '?';
            if (isValuelessOperator(rule.operator)) return `${fieldLabel} ${opLabel}`;
            if (rule.operator === 'between' && Array.isArray(rule.value)) {
                return `${fieldLabel} ${opLabel} ${rule.value[0] ?? '?'} and ${rule.value[1] ?? '?'}`;
            }
            return `${fieldLabel} ${opLabel} ${rule.value ?? '?'}`;
        });
        parts.push(`Conditions: ${condTexts.join('; ')}`);
    }

    if (formData.delayAfterTrigger) {
        parts.push(`Delay: ${formData.delayAfterTrigger}`);
    }

    return parts.join(' | ');
}

// ─── IntervalPicker ─────────────────────────────────────────────────────────

const IntervalPicker = ({ label, value, onChange, presets, testIdPrefix }) => {
    const parsed = useMemo(() => parseIntervalString(value), [value]);

    const handleNumberChange = (e) => {
        const num = e.target.value === '' ? '' : parseInt(e.target.value, 10);
        onChange(buildIntervalString(num, parsed.unit));
    };

    const handleUnitChange = (e) => {
        onChange(buildIntervalString(parsed.value, e.target.value));
    };

    const handlePresetClick = (preset) => {
        if (preset.value === 0) {
            onChange('');
        } else {
            onChange(buildIntervalString(preset.value, preset.unit));
        }
    };

    return (
        <Box data-testid={testIdPrefix}>
            <Typography variant="subtitle2" gutterBottom>{label}</Typography>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 1 }}>
                <TextField
                    size="small"
                    type="number"
                    label="Value"
                    value={parsed.value}
                    onChange={handleNumberChange}
                    inputProps={{ min: 0 }}
                    sx={{ width: 120 }}
                    data-testid={`${testIdPrefix}-value`}
                />
                <TextField
                    size="small"
                    select
                    label="Unit"
                    value={parsed.unit}
                    onChange={handleUnitChange}
                    sx={{ width: 120 }}
                    data-testid={`${testIdPrefix}-unit`}
                >
                    {TIME_UNITS.map(u => (
                        <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>
                    ))}
                </TextField>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {presets.map((preset) => {
                    const presetStr = preset.value === 0 ? '' : buildIntervalString(preset.value, preset.unit);
                    const isActive = value === presetStr || (!value && preset.value === 0);
                    return (
                        <Chip
                            key={preset.label}
                            label={preset.label}
                            size="small"
                            variant={isActive ? 'filled' : 'outlined'}
                            color={isActive ? 'primary' : 'default'}
                            onClick={() => handlePresetClick(preset)}
                            sx={{ cursor: 'pointer' }}
                            data-testid={`${testIdPrefix}-preset-${preset.label.replace(/\s/g, '-').toLowerCase()}`}
                        />
                    );
                })}
            </Box>
        </Box>
    );
};

// ─── ConditionRuleRow ───────────────────────────────────────────────────────

const ConditionRuleRow = ({ rule, index, onChange, onDelete, validationErrors }) => {
    const groupedFields = useMemo(getGroupedFieldOptions, []);
    const operators = useMemo(() => getConditionOperatorsForField(rule.field), [rule.field]);
    const condErrors = validationErrors.filter(e => e.key.startsWith(`cond-${index}`));

    const handleFieldChange = (e) => {
        const newField = e.target.value;
        const meta = FIELD_METADATA[newField];
        let newOp = rule.operator;
        let newVal = rule.value;
        if (meta?.type === 'boolean' && !BOOLEAN_OPERATORS.includes(newOp)) {
            newOp = 'is_true';
            newVal = undefined;
        } else if (meta?.type === 'number' && BOOLEAN_OPERATORS.includes(newOp)) {
            newOp = '>';
            newVal = '';
        }
        onChange({ ...rule, field: newField, operator: newOp, value: newVal });
    };

    const handleOperatorChange = (e) => {
        const newOp = e.target.value;
        let newVal = rule.value;
        if (isValuelessOperator(newOp)) {
            newVal = undefined;
        } else if (newOp === 'between' && !Array.isArray(newVal)) {
            newVal = ['', ''];
        } else if (newOp !== 'between' && Array.isArray(newVal)) {
            newVal = '';
        }
        onChange({ ...rule, operator: newOp, value: newVal });
    };

    const handleValueChange = (val) => {
        onChange({ ...rule, value: val });
    };

    const fieldError = condErrors.find(e => e.key.endsWith('-field'));
    const valueError = condErrors.find(e => e.key.endsWith('-value'));
    const dupWarning = condErrors.find(e => e.key.endsWith('-dup'));

    return (
        <Box
            data-testid={`schedule-condition-${index}`}
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5, flexWrap: { xs: 'wrap', md: 'nowrap' } }}
        >
            {/* Field Select */}
            <FormControl
                size="small"
                sx={{ minWidth: 220, flex: '1 1 220px' }}
                error={!!fieldError}
                data-testid={`schedule-condition-field-${index}`}
            >
                <InputLabel>Field</InputLabel>
                <Select
                    value={rule.field || ''}
                    onChange={handleFieldChange}
                    label="Field"
                >
                    {groupedFields.map(({ category, fields }) => [
                        <ListSubheader key={`header-${category}`} sx={{ fontWeight: 700, color: 'text.primary', bgcolor: 'background.default' }}>
                            {CATEGORY_LABELS[category]}
                        </ListSubheader>,
                        ...fields.map(([fieldKey, meta]) => (
                            <MenuItem key={fieldKey} value={fieldKey}>
                                <Box>
                                    <Typography variant="body2">{meta.label}</Typography>
                                    <Typography variant="caption" color="text.secondary">{fieldKey}</Typography>
                                </Box>
                            </MenuItem>
                        )),
                    ])}
                </Select>
                {fieldError && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>{fieldError.message}</Typography>}
            </FormControl>

            {/* Operator Select */}
            <FormControl
                size="small"
                sx={{ minWidth: 160, flex: '0 1 160px' }}
                data-testid={`schedule-condition-operator-${index}`}
            >
                <InputLabel>Operator</InputLabel>
                <Select
                    value={rule.operator || ''}
                    onChange={handleOperatorChange}
                    label="Operator"
                >
                    {operators.map(op => (
                        <MenuItem key={op} value={op}>{CONDITION_OPERATOR_LABELS[op]}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            {/* Value Input */}
            <Box
                sx={{ flex: '1 1 160px', minWidth: 120 }}
                data-testid={`schedule-condition-value-${index}`}
            >
                {isValuelessOperator(rule.operator) ? (
                    <TextField
                        size="small"
                        fullWidth
                        disabled
                        value="(no value needed)"
                        InputProps={{ readOnly: true }}
                    />
                ) : rule.operator === 'between' ? (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                            size="small"
                            type="number"
                            label="Min"
                            value={Array.isArray(rule.value) ? rule.value[0] : ''}
                            onChange={(e) => {
                                const arr = Array.isArray(rule.value) ? [...rule.value] : ['', ''];
                                arr[0] = e.target.value;
                                handleValueChange(arr);
                            }}
                            error={!!valueError}
                            sx={{ flex: 1 }}
                        />
                        <Typography variant="body2" color="text.secondary">-</Typography>
                        <TextField
                            size="small"
                            type="number"
                            label="Max"
                            value={Array.isArray(rule.value) ? rule.value[1] : ''}
                            onChange={(e) => {
                                const arr = Array.isArray(rule.value) ? [...rule.value] : ['', ''];
                                arr[1] = e.target.value;
                                handleValueChange(arr);
                            }}
                            error={!!valueError}
                            sx={{ flex: 1 }}
                        />
                    </Box>
                ) : FIELD_METADATA[rule.field]?.type === 'number' ? (
                    <TextField
                        size="small"
                        fullWidth
                        type="number"
                        label="Value"
                        value={rule.value ?? ''}
                        onChange={(e) => handleValueChange(e.target.value === '' ? '' : Number(e.target.value))}
                        error={!!valueError}
                    />
                ) : (
                    <TextField
                        size="small"
                        fullWidth
                        label="Value"
                        value={rule.value ?? ''}
                        onChange={(e) => handleValueChange(e.target.value)}
                        error={!!valueError}
                    />
                )}
                {valueError && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>{valueError.message}</Typography>}
            </Box>

            {/* Delete button */}
            <IconButton
                size="small"
                onClick={onDelete}
                color="error"
                sx={{ mt: 0.5 }}
                data-testid={`schedule-delete-condition-${index}`}
            >
                <DeleteIcon fontSize="small" />
            </IconButton>

            {/* Duplicate warning */}
            {dupWarning && (
                <Typography variant="caption" color="warning.main" sx={{ width: '100%' }}>
                    {dupWarning.message}
                </Typography>
            )}
        </Box>
    );
};

// ─── ScheduleBuilder (main export) ──────────────────────────────────────────

const ScheduleBuilder = ({ formData, setField }) => {
    const [jsonViewOpen, setJsonViewOpen] = useState(false);
    const [showValidation, setShowValidation] = useState(false);
    const [triggerChangeDialog, setTriggerChangeDialog] = useState(null);

    const triggerType = formData.triggerType || '';
    const visibility = useMemo(() => FIELD_VISIBILITY[triggerType] || {}, [triggerType]);

    const conditionRules = useMemo(() => {
        const rules = formData.conditionRules;
        if (Array.isArray(rules)) return rules;
        return [];
    }, [formData.conditionRules]);

    const validationErrors = useMemo(() => validateScheduleConfig(formData), [formData]);
    const summaryText = useMemo(() => generateTriggerSummary(formData), [formData]);

    // ─── Trigger type change with confirmation ───────────────────────────

    const handleTriggerTypeChange = useCallback((event, newType) => {
        if (!newType || newType === triggerType) return;

        // Check if switching away would lose data
        const hasData =
            (visibility.events && formData.eventTriggers?.length > 0) ||
            (visibility.conditions && conditionRules.length > 0);

        if (hasData) {
            setTriggerChangeDialog(newType);
        } else {
            setField('triggerType', newType);
        }
    }, [triggerType, visibility, formData.eventTriggers, conditionRules, setField]);

    const confirmTriggerChange = useCallback(() => {
        if (triggerChangeDialog) {
            setField('triggerType', triggerChangeDialog);
            setTriggerChangeDialog(null);
        }
    }, [triggerChangeDialog, setField]);

    // ─── Event trigger handlers ──────────────────────────────────────────

    const handleEventTriggerToggle = useCallback((triggerId) => {
        const current = formData.eventTriggers || [];
        const updated = current.includes(triggerId)
            ? current.filter(t => t !== triggerId)
            : [...current, triggerId];
        setField('eventTriggers', updated);
    }, [formData.eventTriggers, setField]);

    // ─── Condition rule handlers ─────────────────────────────────────────

    const handleAddCondition = useCallback(() => {
        setField('conditionRules', [...conditionRules, { field: '', operator: '', value: '' }]);
    }, [conditionRules, setField]);

    const handleConditionChange = useCallback((index, updatedRule) => {
        const newRules = [...conditionRules];
        newRules[index] = updatedRule;
        setField('conditionRules', newRules);
    }, [conditionRules, setField]);

    const handleDeleteCondition = useCallback((index) => {
        setField('conditionRules', conditionRules.filter((_, i) => i !== index));
    }, [conditionRules, setField]);

    // ─── JSON fallback handlers ──────────────────────────────────────────

    const handleJsonSave = useCallback((parsed) => {
        if (parsed && typeof parsed === 'object') {
            if (parsed.triggerType !== undefined) setField('triggerType', parsed.triggerType);
            // Accept both 'interval' and legacy 'scheduleInterval' on JSON import
            if (parsed.interval !== undefined) setField('interval', parsed.interval);
            else if (parsed.scheduleInterval !== undefined) setField('interval', parsed.scheduleInterval);
            if (parsed.eventTriggers !== undefined) setField('eventTriggers', parsed.eventTriggers);
            if (parsed.conditionRules !== undefined) setField('conditionRules', parsed.conditionRules);
            if (parsed.delayAfterTrigger !== undefined) setField('delayAfterTrigger', parsed.delayAfterTrigger);
            if (parsed.quietHoursStart !== undefined) setField('quietHoursStart', parsed.quietHoursStart);
            if (parsed.quietHoursEnd !== undefined) setField('quietHoursEnd', parsed.quietHoursEnd);
        }
    }, [setField]);

    const jsonData = useMemo(() => ({
        triggerType: formData.triggerType,
        interval: formData.interval,
        eventTriggers: formData.eventTriggers,
        conditionRules: formData.conditionRules,
        delayAfterTrigger: formData.delayAfterTrigger,
        quietHoursStart: formData.quietHoursStart || null,
        quietHoursEnd: formData.quietHoursEnd || null,
    }), [formData.triggerType, formData.interval, formData.eventTriggers, formData.conditionRules, formData.delayAfterTrigger, formData.quietHoursStart, formData.quietHoursEnd]);

    const hasErrors = validationErrors.filter(e => e.severity !== 'warning').length > 0;

    return (
        <Paper sx={{ p: 3 }} data-testid="analysis-task-panel-schedule">
            <Typography variant="h6" gutterBottom>Schedule & Triggers</Typography>

            {/* ─── AC1: Trigger Type Selector ──────────────────────────────── */}
            <Box sx={{ mb: 3 }} data-testid="trigger-type-selector">
                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                    Trigger Type
                </Typography>
                <ToggleButtonGroup
                    value={triggerType}
                    exclusive
                    onChange={handleTriggerTypeChange}
                    sx={{ flexWrap: 'wrap' }}
                    data-testid="trigger-type-toggle-group"
                >
                    {TRIGGER_TYPE_OPTIONS.map((opt) => (
                        <ToggleButton
                            key={opt.value}
                            value={opt.value}
                            sx={{
                                px: 2, py: 1.5,
                                textTransform: 'none',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 0.5,
                                minWidth: 120,
                            }}
                            data-testid={`trigger-type-${opt.value}`}
                        >
                            {opt.icon}
                            <Typography variant="body2" fontWeight={600}>{opt.label}</Typography>
                            <Typography variant="caption" color="text.secondary">{opt.description}</Typography>
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Box>

            {/* ─── AC2: Schedule Interval ──────────────────────────────────── */}
            {visibility.interval && (
                <Box sx={{ mb: 3 }} data-testid="schedule-interval-section">
                    <IntervalPicker
                        label={triggerType === 'combination' ? 'Base Schedule Interval' : 'Schedule Interval'}
                        value={formData.interval}
                        onChange={(val) => setField('interval', val)}
                        presets={INTERVAL_PRESETS}
                        testIdPrefix="schedule-interval"
                    />
                </Box>
            )}

            {/* ─── AC3: Event Triggers ─────────────────────────────────────── */}
            {visibility.events && (
                <Box sx={{ mb: 3 }} data-testid="event-triggers-section">
                    <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                        Event Triggers
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Select which events should trigger this analysis task.
                    </Typography>
                    <FormGroup>
                        <Grid container>
                            {AVAILABLE_EVENT_TRIGGERS.map((trigger) => (
                                <Grid item xs={12} sm={6} key={trigger.id}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={(formData.eventTriggers || []).includes(trigger.id)}
                                                onChange={() => handleEventTriggerToggle(trigger.id)}
                                                data-testid={`event-trigger-checkbox-${trigger.id}`}
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography variant="body2">{trigger.label}</Typography>
                                                <Typography variant="caption" color="text.secondary">{trigger.description}</Typography>
                                                <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace', color: 'text.disabled' }}>{trigger.id}</Typography>
                                            </Box>
                                        }
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </FormGroup>
                </Box>
            )}

            {/* ─── AC4: Condition Rules ────────────────────────────────────── */}
            {visibility.conditions && (
                <Box sx={{ mb: 3 }} data-testid="condition-rules-section">
                    <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                        Condition Rules
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Define conditions that must be met for this task to trigger. All conditions are evaluated with AND logic.
                    </Typography>

                    {conditionRules.length === 0 ? (
                        <Alert severity="info" sx={{ mb: 2 }} data-testid="no-conditions-message">
                            No condition rules — add one below.
                        </Alert>
                    ) : (
                        conditionRules.map((rule, i) => (
                            <ConditionRuleRow
                                key={i}
                                rule={rule}
                                index={i}
                                onChange={(updated) => handleConditionChange(i, updated)}
                                onDelete={() => handleDeleteCondition(i)}
                                validationErrors={validationErrors}
                            />
                        ))
                    )}

                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAddCondition}
                        sx={{ mt: 1, textTransform: 'none' }}
                        data-testid="add-condition-rule"
                    >
                        Add Condition
                    </Button>
                </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* ─── AC5: Delay After Trigger ────────────────────────────────── */}
            <Box sx={{ mb: 3 }} data-testid="delay-after-trigger-section">
                <IntervalPicker
                    label="Delay After Trigger"
                    value={formData.delayAfterTrigger}
                    onChange={(val) => setField('delayAfterTrigger', val)}
                    presets={DELAY_PRESETS}
                    testIdPrefix="delay-after-trigger"
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Optional delay between when the trigger fires and when the task executes.
                </Typography>
            </Box>

            {/* ─── Quiet Hours (Issue #83) ──────────────────────────────────── */}
            <Box sx={{ mb: 3 }} data-testid="quiet-hours-section">
                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                    Quiet Hours
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Executions will be skipped during quiet hours (based on user timezone).
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                        label="Quiet Hours Start"
                        type="time"
                        size="small"
                        value={formData.quietHoursStart || ''}
                        onChange={(e) => setField('quietHoursStart', e.target.value || null)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: 1, maxWidth: 200 }}
                        data-testid="quiet-hours-start"
                    />
                    <TextField
                        label="Quiet Hours End"
                        type="time"
                        size="small"
                        value={formData.quietHoursEnd || ''}
                        onChange={(e) => setField('quietHoursEnd', e.target.value || null)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: 1, maxWidth: 200 }}
                        data-testid="quiet-hours-end"
                    />
                </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* ─── AC7: Validation Errors ──────────────────────────────────── */}
            {validationErrors.length > 0 && (
                <Box sx={{ mb: 2 }} data-testid="schedule-validation-errors">
                    <Button
                        size="small"
                        onClick={() => setShowValidation(v => !v)}
                        sx={{ textTransform: 'none', color: hasErrors ? 'error.main' : 'warning.main' }}
                    >
                        {hasErrors
                            ? `${validationErrors.filter(e => e.severity !== 'warning').length} validation error(s)`
                            : `${validationErrors.length} warning(s)`}
                        {showValidation ? <ExpandLessIcon sx={{ ml: 0.5 }} fontSize="small" /> : <ExpandMoreIcon sx={{ ml: 0.5 }} fontSize="small" />}
                    </Button>
                    <Collapse in={showValidation}>
                        <Box sx={{ mt: 1 }}>
                            {validationErrors.map((err) => (
                                <Alert
                                    key={err.key}
                                    severity={err.severity === 'warning' ? 'warning' : 'error'}
                                    sx={{ mb: 0.5, py: 0 }}
                                >
                                    {err.message}
                                </Alert>
                            ))}
                        </Box>
                    </Collapse>
                </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* ─── AC8: Trigger Summary ────────────────────────────────────── */}
            <Box sx={{ mb: 3 }} data-testid="trigger-summary">
                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                    Trigger Summary
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2">{summaryText}</Typography>
                </Paper>
            </Box>

            {/* ─── AC9: JSON Fallback ──────────────────────────────────────── */}
            <Box data-testid="schedule-json-toggle">
                <Button
                    size="small"
                    onClick={() => setJsonViewOpen(v => !v)}
                    startIcon={jsonViewOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ textTransform: 'none', color: 'text.secondary', mb: 1 }}
                >
                    Advanced: JSON View
                </Button>
                <Collapse in={jsonViewOpen}>
                    <JsonViewTab
                        data={jsonData}
                        onSave={handleJsonSave}
                    />
                </Collapse>
            </Box>

            {/* ─── Trigger Type Change Confirmation Dialog ─────────────────── */}
            <Dialog
                open={!!triggerChangeDialog}
                onClose={() => setTriggerChangeDialog(null)}
                data-testid="trigger-change-confirm-dialog"
            >
                <DialogTitle>Change Trigger Type?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Changing the trigger type may hide some of your configured settings (event triggers or condition rules).
                        The data will be preserved but may not be visible until you switch back.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTriggerChangeDialog(null)}>Cancel</Button>
                    <Button
                        onClick={confirmTriggerChange}
                        variant="contained"
                        data-testid="trigger-change-confirm-btn"
                    >
                        Change
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export { AVAILABLE_EVENT_TRIGGERS, CONDITION_OPERATOR_LABELS, generateTriggerSummary, validateScheduleConfig };
export default ScheduleBuilder;
